import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GroupsIcon from "@mui/icons-material/Groups";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import ScienceIcon from "@mui/icons-material/Science";
import TableChartIcon from "@mui/icons-material/TableChart";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import { BreadcrumbsTable } from "../../../components/common/HelperFunc";

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthStart = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const local = new Date(start.getTime() - start.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const REPORT_SECTIONS = [
  {
    id: "risk",
    title: "위험성평가 결과",
    helper: "유해·위험요인, 등급, 개선대책 요약",
    icon: <InsertChartIcon />,
    color: "#2563eb",
  },
  {
    id: "strategy",
    title: "개선대책 이행",
    helper: "대책 수립, 진행률, 지연 항목",
    icon: <AssignmentIcon />,
    color: "#0f766e",
  },
  {
    id: "tbm",
    title: "작업 전 안전점검회의(TBM)",
    helper: "실시율, 참석률, 미실시 팀",
    icon: <GroupsIcon />,
    color: "#7c3aed",
  },
  {
    id: "education",
    title: "안전보건교육",
    helper: "교육 일정, 대상자, 이수 현황",
    icon: <SchoolIcon />,
    color: "#0891b2",
  },
  {
    id: "inspection",
    title: "현장 안전점검",
    helper: "점검 결과, 조치요청, 기한 추적",
    icon: <FactCheckIcon />,
    color: "#ea580c",
  },
  {
    id: "msds",
    title: "물질안전보건자료(MSDS) 관리 현황",
    helper: "등록 현황, 만료 예정, 주요 물질",
    icon: <ScienceIcon />,
    color: "#dc2626",
  },
  {
    id: "legal_cert",
    title: "설비·구역 검사·인증 관리",
    helper: "법정 검사, 인증서, 유효기간 관리",
    icon: <VerifiedUserIcon />,
    color: "#4f46e5",
  },
  {
    id: "committee",
    title: "안전보건위원회",
    helper: "위원회 구성, 회의록과 첨부자료",
    icon: <GroupsIcon />,
    color: "#7c3aed",
  },
];

const TEMPLATE_OPTIONS = [
  { value: "executive", label: "경영진 요약형" },
  { value: "audit", label: "감사 대응형" },
  { value: "site", label: "현장 공유형" },
];

const OUTPUT_FORMATS = [
  { value: "xlsx", label: "Excel", icon: <TableChartIcon /> },
  { value: "pdf", label: "PDF", icon: <DescriptionIcon /> },
];

export function IntegratedReportGenerator() {
  const theme = useTheme();
  const isTabletPortrait = useMediaQuery(
    "(orientation: portrait) and (min-width: 900px) and (max-width: 1400px)"
  );
  const { group_code, project_code } = useParams();
  const [startDate, setStartDate] = useState(getMonthStart());
  const [endDate, setEndDate] = useState(getToday());
  const [template, setTemplate] = useState("executive");
  const [reportName, setReportName] = useState("월간 안전보건 통합 보고서");
  const [formats, setFormats] = useState(["xlsx", "pdf"]);
  const [includeEvidence] = useState(true);
  const [includeCharts] = useState(true);
  const [autoSummary] = useState(true);
  const [selectedSectionIds, setSelectedSectionIds] = useState(
    REPORT_SECTIONS.map((section) => section.id)
  );
  const [lastGeneratedAt, setLastGeneratedAt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [generatedReport, setGeneratedReport] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [sectionFilter, setSectionFilter] = useState("");

  const selectedSections = useMemo(
    () => REPORT_SECTIONS.filter((section) => selectedSectionIds.includes(section.id)),
    [selectedSectionIds]
  );

  const excelFile = generatedReport?.files?.find((file) => file.format === "xlsx");
  const pdfFile = generatedReport?.files?.find((file) => file.format === "pdf");
  const showExcelDownload = formats.includes("xlsx");
  const showPdfDownload = formats.includes("pdf");
  const allSectionsSelected = selectedSectionIds.length === REPORT_SECTIONS.length;
  const partiallySelected =
    selectedSectionIds.length > 0 && selectedSectionIds.length < REPORT_SECTIONS.length;
  const filteredSections = useMemo(() => {
    const keyword = sectionFilter.trim().toLowerCase();
    if (!keyword) return REPORT_SECTIONS;
    return REPORT_SECTIONS.filter((section) =>
      [section.title, section.helper]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [sectionFilter]);
  const getDownloadUrl = (file) =>
    file?.download_url ? `${axiosInstance.defaults.baseURL}${file.download_url}` : "";

  const toggleSection = (sectionId) => {
    setSelectedSectionIds((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleAllSections = (checked) => {
    setSelectedSectionIds(checked ? REPORT_SECTIONS.map((section) => section.id) : []);
  };

  const handleFormatChange = (event, nextFormats) => {
    if (nextFormats.length) {
      setFormats(nextFormats);
    }
  };

  const handleGenerate = async () => {
    if (!group_code || !project_code) {
      setGenerateError("프로젝트 정보가 없어 보고서를 생성할 수 없습니다.");
      return;
    }

    setIsGenerating(true);
    setGenerateError("");

    try {
      const response = await axiosInstance.post(
        `/risk/${group_code}/${project_code}/integrated-report/generate`,
        {
          report_name: reportName.trim() || "월간 안전보건 통합 보고서",
          start_date: startDate,
          end_date: endDate,
          template,
          formats,
          sections: selectedSectionIds,
          include_evidence: includeEvidence,
          include_charts: includeCharts,
          auto_summary: autoSummary,
        }
      );
      const result = response.data?.data;
      setGeneratedReport(result);
      setLastGeneratedAt(result?.generated_at || new Date().toLocaleString("ko-KR"));
    } catch (error) {
      setGeneratedReport(null);
      setGenerateError(
        error.response?.data?.message || "통합 보고서 생성 중 오류가 발생했습니다."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box className="safe-page-root"
      sx={{
        bgcolor: "var(--mui-palette-background-paper)",
        height: { xs: "auto", lg: "100%" },
        minHeight: { xs: "100%", lg: 0 },
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <BreadcrumbsTable parentTitle="보고서" childTitle="통합 보고서 자동생성" />

      <Box className="safe-page-body"
        sx={{
          width: "100%",
          flex: { xs: "0 0 auto", lg: 1 },
          minHeight: 0,
          minWidth: 0,
          p: { xs: "12px", md: "16px 32px" },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: { xs: "visible", lg: "hidden" },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: isTabletPortrait ? "column" : "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: isTabletPortrait ? "stretch" : "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon color="primary" />
                <Typography sx={{ fontWeight: 900 }}>보고서 조건</Typography>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent={{ sm: isTabletPortrait ? "flex-end" : "initial" }}
              >
                <ToggleButtonGroup
                  value={formats}
                  onChange={handleFormatChange}
                  size="small"
                  color="primary"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    flexWrap: "wrap",
                    "& .MuiToggleButton-root": {
                      flex: { xs: "1 1 0", sm: "0 0 auto" },
                      minWidth: { xs: 0, sm: 82 },
                    },
                  }}
                >
                  {OUTPUT_FORMATS.map((format) => (
                    <ToggleButton key={format.value} value={format.value}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {format.icon}
                        <span>{format.label}</span>
                      </Stack>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Button
                  variant="contained"
                  startIcon={
                    isGenerating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  onClick={handleGenerate}
                  disabled={!selectedSections.length || !formats.length || isGenerating}
                  sx={{
                    minWidth: { xs: "100%", sm: 132 },
                    fontWeight: 900,
                  }}
                >
                  {isGenerating ? "생성 중" : "보고서 생성"}
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
              <Grid item xs={12} md={isTabletPortrait ? 4 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  label="보고서명"
                  value={reportName}
                  onChange={(event) => setReportName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isTabletPortrait ? 2.5 : 2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="시작일"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isTabletPortrait ? 2.5 : 2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="종료일"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isTabletPortrait ? 3 : 2}>
                <FormControl fullWidth size="small">
                  <InputLabel>보고서 유형</InputLabel>
                  <Select
                    label="보고서 유형"
                    value={template}
                    onChange={(event) => setTemplate(event.target.value)}
                  >
                    {TEMPLATE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* 요약/차트/근거 옵션은 현재 시연 화면에서 숨김 처리합니다. */}
            </Grid>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: { xs: "0 0 auto", lg: 1 },
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Checkbox
                checked={allSectionsSelected}
                indeterminate={partiallySelected}
                onChange={(event) => toggleAllSections(event.target.checked)}
                size="small"
              />
              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>전체 선택</Typography>
              <Chip size="small" label={`선택 ${selectedSections.length}/${REPORT_SECTIONS.length}`} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
              <IconButton onClick={() => setShowSearch((prev) => !prev)}>
                <SearchIcon />
              </IconButton>
              <Collapse in={showSearch} orientation="horizontal" sx={{ minWidth: 0 }}>
                <TextField
                  size="small"
                  placeholder="항목 검색"
                  value={sectionFilter}
                  onChange={(event) => setSectionFilter(event.target.value)}
                  sx={{ width: 200, maxWidth: "100%" }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setSectionFilter("");
                            setShowSearch(false);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Collapse>
            </Stack>
          </Box>

          <Box sx={{ flex: { xs: "0 0 auto", lg: 1 }, minHeight: 0, overflow: "auto", maxWidth: "100%" }}>
            <Box sx={{ minWidth: { xs: 0, md: 980 }, width: "100%" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "48px minmax(0, 1fr)",
                    md: "64px 260px minmax(300px, 1fr) 170px 130px",
                  },
                  alignItems: "center",
                  minHeight: 42,
                  bgcolor: "#fafafa",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {["", "보고서 항목", "구성 내용", "출력 형식", "포함 여부"].map((header, index) => (
                  <Typography
                    key={header || "select"}
                    sx={{
                      px: 2,
                      fontSize: 13,
                      fontWeight: 900,
                      color: theme.palette.text.primary,
                      textAlign: header ? "center" : "left",
                      display: { xs: index > 1 ? "none" : "block", md: "block" },
                    }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>

              {filteredSections.map((section) => {
                const selected = selectedSectionIds.includes(section.id);
                return (
                  <Box
                    key={section.id}
                    onClick={() => toggleSection(section.id)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "48px minmax(0, 1fr)",
                        md: "64px 260px minmax(300px, 1fr) 170px 130px",
                      },
                      alignItems: "center",
                      minHeight: 64,
                      cursor: "pointer",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      bgcolor: selected ? `${section.color}0a` : "#fff",
                      "&:hover": { bgcolor: selected ? `${section.color}12` : "#fafafa" },
                    }}
                  >
                    <Box sx={{ px: 2 }}>
                      <Checkbox
                        checked={selected}
                        size="small"
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSection(section.id)}
                      />
                    </Box>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 2, minWidth: 0 }}>
                      <Box sx={{ color: section.color, display: "flex", alignItems: "center" }}>
                        {section.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {section.title}
                      </Typography>
                    </Stack>
                    <Typography sx={{ px: 2, color: "text.secondary", fontSize: 14, display: { xs: "none", md: "block" } }}>
                      {section.helper}
                    </Typography>
                    <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ px: 2, display: { xs: "none", md: "flex" } }}>
                      {formats.map((format) => (
                        <Chip
                          key={format}
                          size="small"
                          color={format === "pdf" ? "secondary" : "success"}
                          label={format.toUpperCase()}
                          sx={{ fontWeight: 800 }}
                        />
                      ))}
                    </Stack>
                    <Box sx={{ px: 2, textAlign: "center", display: { xs: "none", md: "block" } }}>
                      <Chip
                        size="small"
                        color={selected ? "success" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        label={selected ? "포함" : "제외"}
                      />
                    </Box>
                  </Box>
                );
              })}

              {filteredSections.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
                  검색 결과가 없습니다.
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              minHeight: 56,
              p: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" }, overflowWrap: "anywhere" }}>
              {generateError ? (
                <Alert severity="error" sx={{ py: 0 }}>{generateError}</Alert>
              ) : generatedReport ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip icon={<CheckCircleIcon />} size="small" color="success" label="생성 완료" />
                  <Typography variant="body2" color="text.secondary">
                    마지막 생성 시각: {lastGeneratedAt}
                  </Typography>
                  <Chip
                    size="small"
                    color="primary"
                    label={`평가 ${generatedReport.summary?.risk_eval_count ?? 0}건`}
                  />
                  <Chip
                    size="small"
                    color="success"
                    label={`개선 이행률 ${Number(generatedReport.summary?.improve_done_rate || 0).toFixed(1)}%`}
                  />
                </Stack>
              ) : (
                null
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              flexShrink={0}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {showExcelDownload && (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={!getDownloadUrl(excelFile)}
                  href={getDownloadUrl(excelFile) || undefined}
                  component="a"
                  download={excelFile?.file_name}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Excel 다운로드
                </Button>
              )}
              {showPdfDownload && (
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  disabled={!getDownloadUrl(pdfFile)}
                  href={getDownloadUrl(pdfFile) || undefined}
                  component="a"
                  download={pdfFile?.file_name}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  PDF 다운로드
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
